import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  CreditCard, 
  User, 
  MapPin, 
  ArrowLeft,
  Heart,
  Code,
  Activity,
  Clock,
  ShoppingCart,
  Lock
} from 'lucide-react'
import ApiMonitor from '../components/ApiMonitor'
import { useApiLogs } from '../contexts/ApiLogsContext'

interface PaymentInfo {
  cardNumber: string
  expiryDate: string
  cvv: string
  cardholderName: string
}

interface BillingAddress {
  street: string
  city: string
  state: string
  zipCode: string
  country: string
}

interface CustomerInfo {
  name: string
  email: string
  phone: string
}

interface ApiLog {
  request: any
  response: any
  loading: boolean
  error: string | null
}

const PaymentConfirmationPage: React.FC = () => {
  const navigate = useNavigate()
  const { addApiLog, updateApiLog } = useApiLogs()
  
  // Form state
  const [demoMode, setDemoMode] = useState(true)
  const [showApiPanel, setShowApiPanel] = useState(false)
  const [sidebarWidth, setSidebarWidth] = useState(480)
  const [sessionDetailsLog, setSessionDetailsLog] = useState<ApiLog | null>(null)
  
  // Payment information state
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo>({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: ''
  })
  
  // Customer information state
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    name: '',
    email: '',
    phone: ''
  })
  
  // Billing address state
  const [billingAddress, setBillingAddress] = useState<BillingAddress>({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: ''
  })
  
  // Dummy data
  const dummyPaymentInfo = {
    cardNumber: '4532 1234 5678 9012',
    expiryDate: '12/28',
    cvv: '123',
    cardholderName: 'Sarah Johnson'
  }
  
  const dummyCustomerInfo = {
    name: 'Sarah Johnson',
    email: 'sarah.johnson@email.com',
    phone: '+1 (555) 123-4567'
  }
  
  const dummyBillingAddress = {
    street: '123 Flower Street',
    city: 'New York',
    state: 'NY',
    zipCode: '10001',
    country: 'United States'
  }
  
  // API configuration (use actual values or fallback to demo values)
  const location = useLocation();
  const state = location.state || {};
  const apiKey = state.apiKey || 'demo-api-key';
  const pubUserId = state.pubUserId || 'demo-pub-user-id';
  const sessionId = state.sessionId || sessionStorage.getItem('usp_session_id') || 'demo-session-id';
  
  // Store session ID in sessionStorage for persistence across page refreshes
  useEffect(() => {
    if (state.sessionId) {
      sessionStorage.setItem('usp_session_id', state.sessionId);
    }
  }, [state.sessionId]);

  // Auto-fill dummy data when demo mode is enabled
  useEffect(() => {
    if (demoMode) {
      setPaymentInfo(dummyPaymentInfo)
      setCustomerInfo(dummyCustomerInfo)
      setBillingAddress(dummyBillingAddress)
    } else {
      // Clear all data when demo mode is disabled
      setPaymentInfo({ cardNumber: '', expiryDate: '', cvv: '', cardholderName: '' })
      setCustomerInfo({ name: '', email: '', phone: '' })
      setBillingAddress({ street: '', city: '', state: '', zipCode: '', country: '' })
    }
  }, [demoMode])

  // Get session details (real API implementation)
  const getSessionDetails = async () => {
    const url = `https://api.adspostx.com/sdk/v4/usp/session/${sessionId}.json?pub_user_id=${pubUserId}`
    const headers = { 
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json'
    }

    // Create initial log entry
    const requestData = {
      url,
      method: 'GET',
      headers,
      timestamp: new Date().toISOString()
    }

    // Add to shared API logs
    const logId = addApiLog({
      page: 'payment-confirmation',
      action: 'GET /sdk/v4/usp/session/{sessionId}.json',
      request: requestData,
      response: null,
      loading: true,
      error: null
    })

    // Set loading state for local component
    setSessionDetailsLog({
      request: requestData,
      response: null,
      loading: true,
      error: null
    })

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers
      })

      const data = await response.json()
      
      const responseData = {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        data,
        timestamp: new Date().toISOString()
      }

      // Update shared API logs
      updateApiLog(logId, {
        response: responseData,
        loading: false,
        error: null
      })

      // Update local state
      setSessionDetailsLog({
        request: requestData,
        response: responseData,
        loading: false,
        error: null
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      
      // Update shared API logs with error
      updateApiLog(logId, {
        response: null,
        loading: false,
        error: errorMessage
      })

      // Update local state
      setSessionDetailsLog({
        request: requestData,
        response: null,
        loading: false,
        error: errorMessage
      })
    }
  }
  
  // Toggle demo mode
  const toggleDemoMode = () => {
    setDemoMode(!demoMode)
  }
  
  // Handle form submissions
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault()
    }
    
    // Validate required fields
    if (!paymentInfo.cardNumber || !paymentInfo.expiryDate || !paymentInfo.cvv || !paymentInfo.cardholderName) {
      alert('Please fill in all payment information fields')
      return
    }
    
    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      alert('Please fill in all customer information fields')
      return
    }
    
    if (!billingAddress.street || !billingAddress.city || !billingAddress.state || !billingAddress.zipCode || !billingAddress.country) {
      alert('Please fill in all billing address fields')
      return
    }
    
    // Call wrap API before completing order
    const wrapUrl = `https://api.adspostx.com/sdk/v4/usp/session/${sessionId}.json`
    const wrapHeaders = {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json'
    }

    const wrapRequestBody = {
      pub_user_id: pubUserId
    }

    const wrapRequestData = {
      method: 'POST',
      url: wrapUrl,
      headers: wrapHeaders,
      body: wrapRequestBody,
      timestamp: new Date().toISOString()
    }

    // Add to shared API logs with session tab classification
    const wrapLogId = addApiLog({
      page: 'usp-demo',
      action: 'POST /sdk/v4/usp/session/{sessionId}.json (Wrap API)',
      request: wrapRequestData,
      response: null,
      loading: true,
      error: null
    })

    try {
      const wrapResponse = await fetch(wrapUrl, {
        method: 'POST',
        headers: wrapHeaders,
        body: JSON.stringify(wrapRequestBody)
      })

      const wrapData = await wrapResponse.json()
      
      const wrapResponseData = {
        status: wrapResponse.status,
        statusText: wrapResponse.statusText,
        headers: Object.fromEntries(wrapResponse.headers.entries()),
        data: wrapData,
        timestamp: new Date().toISOString()
      }

      // Update shared API logs
      updateApiLog(wrapLogId, {
        response: wrapResponseData,
        loading: false,
        error: null
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      
      // Update shared API logs with error
      updateApiLog(wrapLogId, {
        response: null,
        loading: false,
        error: errorMessage
      })
    }
    
    // Create order data
    const orderData = {
      orderNumber: `ORD-${Date.now()}`,
      paymentInfo,
      customerInfo,
      billingAddress,
      orderTotal: 131.72,
      timestamp: new Date().toISOString(),
      sessionId,
      pubUserId,
      apiKey
    }
    
    // Navigate to final confirmation page
    navigate('/final-confirmation', { state: orderData })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-pink-100 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-[#FE4215] rounded-lg flex items-center justify-center">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">Payment Confirmation</h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Complete your flower order</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden lg:flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                  <Clock className="w-4 h-4" />
                  <span className="font-medium">Session ID:</span>
                  <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">
                    {sessionId}
                  </code>
                </div>
                <div className="w-px h-4 bg-gray-300 dark:bg-gray-600"></div>
                <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                  <span className="font-medium">User ID:</span>
                  <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">
                    {pubUserId}
                  </code>
                </div>
              </div>
              <button
                onClick={() => setShowApiPanel(!showApiPanel)}
                className={`flex items-center space-x-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                  showApiPanel
                    ? 'bg-pink-100 dark:bg-pink-900/20 text-pink-700 dark:text-pink-300'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <Code className="w-4 h-4" />
                <span>API</span>
                {sessionDetailsLog?.loading && (
                  <Activity className="w-4 h-4 animate-spin" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Payment Form */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-pink-100 dark:border-gray-700 p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Payment Details</h2>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Demo Mode</span>
                  <button
                    onClick={toggleDemoMode}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      demoMode
                        ? 'bg-pink-600'
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        demoMode ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Cart and Perks Adjustment Notice */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                        <ArrowLeft className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-2">
                        Need to make changes?
                      </h3>
                      <p className="text-blue-700 dark:text-blue-300 text-sm mb-4">
                        You can go back and adjust your cart items or modify your amazing perks selection before completing your payment.
                      </p>
                      <div className="flex justify-center">
                        <button
                          type="button"
                          onClick={() => navigate('/usp-demo')}
                          className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors shadow-lg hover:shadow-xl"
                        >
                          <ArrowLeft className="w-4 h-4" />
                          <span>Go Back & Adjust Cart or Perks</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
                    <CreditCard className="w-5 h-5 mr-2 text-pink-600 dark:text-pink-400" />
                    Payment Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Cardholder Name
                      </label>
                      <input
                        type="text"
                        value={paymentInfo.cardholderName}
                        onChange={(e) => setPaymentInfo({...paymentInfo, cardholderName: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter cardholder name"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Card Number
                      </label>
                      <input
                        type="text"
                        value={paymentInfo.cardNumber}
                        onChange={(e) => setPaymentInfo({...paymentInfo, cardNumber: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="1234 5678 9012 3456"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Expiry Date
                      </label>
                      <input
                        type="text"
                        value={paymentInfo.expiryDate}
                        onChange={(e) => setPaymentInfo({...paymentInfo, expiryDate: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="MM/YY"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        CVV
                      </label>
                      <input
                        type="text"
                        value={paymentInfo.cvv}
                        onChange={(e) => setPaymentInfo({...paymentInfo, cvv: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="123"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Customer Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
                    <User className="w-5 h-5 mr-2 text-pink-600 dark:text-pink-400" />
                    Customer Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={customerInfo.name}
                        onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter full name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={customerInfo.email}
                        onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter email"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={customerInfo.phone}
                        onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter phone number"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Billing Address */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
                    <MapPin className="w-5 h-5 mr-2 text-pink-600 dark:text-pink-400" />
                    Billing Address
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Street Address
                      </label>
                      <input
                        type="text"
                        value={billingAddress.street}
                        onChange={(e) => setBillingAddress({...billingAddress, street: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter street address"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        City
                      </label>
                      <input
                        type="text"
                        value={billingAddress.city}
                        onChange={(e) => setBillingAddress({...billingAddress, city: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter city"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        State
                      </label>
                      <input
                        type="text"
                        value={billingAddress.state}
                        onChange={(e) => setBillingAddress({...billingAddress, state: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter state"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        ZIP Code
                      </label>
                      <input
                        type="text"
                        value={billingAddress.zipCode}
                        onChange={(e) => setBillingAddress({...billingAddress, zipCode: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter ZIP code"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Country
                      </label>
                      <input
                        type="text"
                        value={billingAddress.country}
                        onChange={(e) => setBillingAddress({...billingAddress, country: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter country"
                        required
                      />
                    </div>
                  </div>
                </div>

              </form>
            </motion.div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-pink-100 dark:border-gray-700 p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <ShoppingCart className="w-5 h-5 mr-2 text-pink-600 dark:text-pink-400" />
                Order Summary
              </h3>
              
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Red Rose Bouquet</span>
                  <span className="text-gray-900 dark:text-white">$45.99</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Spring Mixed Bouquet × 2</span>
                  <span className="text-gray-900 dark:text-white">$65.98</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
                  <span className="text-gray-900 dark:text-white">$111.97</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Shipping</span>
                  <span className="text-gray-900 dark:text-white">$9.99</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Tax</span>
                  <span className="text-gray-900 dark:text-white">$9.76</span>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
                  <div className="flex justify-between text-lg font-semibold">
                    <span className="text-gray-900 dark:text-white">Total</span>
                    <span className="text-gray-900 dark:text-white">$131.72</span>
                  </div>
                </div>
                
                {/* Complete Order Button */}
                <div className="mt-6">
                  <button
                    onClick={() => handleSubmit()}
                    className="w-full bg-[#FE4215] hover:bg-[#E53910] text-white font-semibold py-4 px-6 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center space-x-3"
                  >
                    <Lock className="w-5 h-5" />
                    <span>Complete Order</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* API Monitor Sidebar */}
      <ApiMonitor
        isVisible={showApiPanel}
        onClose={() => setShowApiPanel(false)}
        width={sidebarWidth}
        onWidthChange={setSidebarWidth}
        sessionId={sessionId}
        pubUserId={pubUserId}
        apiKey={apiKey}
        onGetSessionDetails={getSessionDetails}
        sessionDetailsLog={sessionDetailsLog}
        uspAllOffersChecked={null}
        hiddenTabs={['all-logs']}
      />
    </div>
  )
}

export default PaymentConfirmationPage
